/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { CourseActivityTaskReq } from './course-activity-task-req'
import { CourseActivityTask } from './course-activity-task'
import { Task } from './task'
import { TaskReflection } from './task-reflection'
import { QuizTaskReviewedReq } from './quiz-task-reviewed-req'
import { ReflectionAnswer } from './reflection-answer'
import { SortBlock } from './sort-block'
import { OutlineReq } from './outline-req'
import { Outline } from './outline'
import { ExerciserReviewed } from './exerciser-reviewed'
import { CourseOutlineTaskReq } from './course-outline-task-req'
import { CourseOutlineTask } from './course-outline-task'
import { CourseActivityTaskMoveReq } from './course-activity-task-move-req'
import { OutlineTaskCompletedReq } from './outline-task-completed-req'
import { LoadCourseActivityTaskQuery } from './load-course-activity-task-query'
import { QueryOutlineTaskQuery } from './query-outline-task-query'
import { QueryCourseOutlineTaskReflectionsQuery } from './query-course-outline-task-reflections-query'
import { ExportCourseOutlineTaskReflectionsQuery } from './export-course-outline-task-reflections-query'
import { QueryCourseReflectionCompletedQuery } from './query-course-reflection-completed-query'

const buildConfig = createConfigBuilder('growp')

export const courseApi = {
  createCourseActivityTaskConfig(
    courseActivityTaskReq: CourseActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/course/activity-tasks',
      {},
      null,
      courseActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          outlines: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】课程-创建活动任务
   *  - 课程目录关联试卷，根据选择试卷拷贝试卷
   * @param courseActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<CourseActivityTask>
   */
  createCourseActivityTask(
    courseActivityTaskReq: CourseActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<CourseActivityTask> {
    return axios(
      courseApi.createCourseActivityTaskConfig(courseActivityTaskReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new CourseActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateCourseActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: CourseActivityTaskReq) =>
        courseApi.createCourseActivityTask(payload),
      mutationOptions
    )
  },

  updateCourseActivityTaskConfig(
    id: number,
    courseActivityTaskReq: CourseActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/activity-tasks/{id}',
      {
        id,
      },
      null,
      courseActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          outlines: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】课程-编辑活动任务
   *  - 更新任务
   *  - 更新目录[不支持]
   *  - 更新目录任务[不支持]
   * @param id
   * @param courseActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<CourseActivityTask>
   */
  updateCourseActivityTask(
    id: number,
    courseActivityTaskReq: CourseActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<CourseActivityTask> {
    return axios(
      courseApi.updateCourseActivityTaskConfig(
        id,
        courseActivityTaskReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new CourseActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateCourseActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: CourseActivityTaskReq }) =>
        courseApi.updateCourseActivityTask(id, payload),
      mutationOptions
    )
  },

  deleteCourseActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/course/activity-tasks/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】课程-删除活动任务
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteCourseActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(courseApi.deleteCourseActivityTaskConfig(id, options)) as any
  },

  useDeleteCourseActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => courseApi.deleteCourseActivityTask(id),
      mutationOptions
    )
  },

  loadCourseActivityTaskConfig(
    id: number,
    loadCourseActivityTaskQuery?: LoadCourseActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/course/activity-tasks/{id}',
      {
        id,
      },
      loadCourseActivityTaskQuery
    )
  },

  /**
   * @summary 【前/后台】课程-查询活动任务
   *  - 后台：活动任务+目录+目录任务+各目录下任务完成人数情况
   *  - 前台：活动任务+目录+目录任务+活动任务完成情况
   * @param id
   * @param [loadCourseActivityTaskQuery]
   * @param [loadCourseActivityTaskQuery.expand] expand
   *  - 活动任务关联的活动人员 task.task_reflections
   *  - 活动任务关联的管理人员 task.admin_reflections
   *  - 课程任务关联的任务 outline.tasks
   * @param [loadCourseActivityTaskQuery.isForeground] 是否是前台请求
   *  - 是：返回-活动任务完成情况
   *  - 否：不返回-活动任务完成情况
   * @param [loadCourseActivityTaskQuery.foregroundRid] 前台请求用户 id
   *  - 前台请求时，如本字段有值，查询该用户数据，否则查询当前 token 对应用户数据
   * @param [options]
   * @return AxiosResponsePromise<CourseActivityTask>
   */
  loadCourseActivityTask<TQuery extends LoadCourseActivityTaskQuery>(
    id: number,
    loadCourseActivityTaskQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<CourseActivityTask, TQuery['expand']>> {
    return axios(
      courseApi.loadCourseActivityTaskConfig(
        id,
        loadCourseActivityTaskQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new CourseActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadCourseActivityTask<TQuery extends LoadCourseActivityTaskQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<CourseActivityTask, TQuery['expand']>> {
    return useQueryApi<Expand<CourseActivityTask, TQuery['expand']>>(
      courseApi.loadCourseActivityTask,
      'Course.loadCourseActivityTask',
      queryOptions,
      id,
      query
    )
  },

  queryOutlineTaskConfig(
    id: number,
    queryOutlineTaskQuery?: QueryOutlineTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/course/activity-tasks/{id}/exercise',
      {
        id,
      },
      queryOutlineTaskQuery
    )
  },

  /**
   * @summary 【后台】课程任务-查询课程下的习题
   * @param id
   * @param [queryOutlineTaskQuery]
   * @param [queryOutlineTaskQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Task[]>
   */
  queryOutlineTask<TQuery extends QueryOutlineTaskQuery>(
    id: number,
    queryOutlineTaskQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Task, TQuery['expand']>[]> {
    return axios(
      courseApi.queryOutlineTaskConfig(id, queryOutlineTaskQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Task(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryOutlineTask<TQuery extends QueryOutlineTaskQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Task, TQuery['expand']>[]> {
    return useQueryApi<Expand<Task, TQuery['expand']>[]>(
      courseApi.queryOutlineTask,
      'Course.queryOutlineTask',
      queryOptions,
      id,
      query
    )
  },

  queryCourseOutlineTaskReflectionsConfig(
    id: number,
    queryCourseOutlineTaskReflectionsQuery?: QueryCourseOutlineTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/course/activity-tasks/{id}/reflections',
      {
        id,
      },
      queryCourseOutlineTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】课程任务-课程数据-任务人员列表(完成情况)
   * @param id
   * @param [queryCourseOutlineTaskReflectionsQuery]
   * @param [queryCourseOutlineTaskReflectionsQuery.expand]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.nameIn]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.nameLike]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.accountLike]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.accountIn]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.genderIn]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.statusIn]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.phoneIn]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.phoneLike]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.emailIn]
   * @param [queryCourseOutlineTaskReflectionsQuery.reflection.emailLike]
   * @param [queryCourseOutlineTaskReflectionsQuery.endAtEgt] 课程任务-完成时间-大于
   * @param [queryCourseOutlineTaskReflectionsQuery.endAtElt] 课程任务-完成时间-小于
   * @param [queryCourseOutlineTaskReflectionsQuery.completedTaskSumElt] 课程目录任务-完成数量-小于
   * @param [queryCourseOutlineTaskReflectionsQuery.completedTaskSumEgt] 课程目录任务-完成数量-大于
   * @param [queryCourseOutlineTaskReflectionsQuery.sort]
   * @param [queryCourseOutlineTaskReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryCourseOutlineTaskReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryCourseOutlineTaskReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryCourseOutlineTaskReflections<
    TQuery extends QueryCourseOutlineTaskReflectionsQuery
  >(
    id: number,
    queryCourseOutlineTaskReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<TaskReflection, TQuery['expand']>[]> {
    return axios(
      courseApi.queryCourseOutlineTaskReflectionsConfig(
        id,
        queryCourseOutlineTaskReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryCourseOutlineTaskReflections<
    TQuery extends QueryCourseOutlineTaskReflectionsQuery
  >(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<TaskReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<TaskReflection, TQuery['expand']>[]>(
      courseApi.queryCourseOutlineTaskReflections,
      'Course.queryCourseOutlineTaskReflections',
      queryOptions,
      id,
      query
    )
  },

  exportCourseOutlineTaskReflectionsConfig(
    id: number,
    exportCourseOutlineTaskReflectionsQuery?: ExportCourseOutlineTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/course/activity-tasks/{id}/reflections/export',
      {
        id,
      },
      exportCourseOutlineTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】课程任务-课程数据-任务人员列表(完成情况)
   * @param id
   * @param [exportCourseOutlineTaskReflectionsQuery]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.nameIn]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.nameLike]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.accountLike]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.accountIn]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.genderIn]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.statusIn]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.phoneIn]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.phoneLike]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.emailIn]
   * @param [exportCourseOutlineTaskReflectionsQuery.reflection.emailLike]
   * @param [exportCourseOutlineTaskReflectionsQuery.endAtEgt] 课程任务-完成时间-大于
   * @param [exportCourseOutlineTaskReflectionsQuery.endAtElt] 课程任务-完成时间-小于
   * @param [exportCourseOutlineTaskReflectionsQuery.completedTaskSumElt] 课程目录任务-完成数量-小于
   * @param [exportCourseOutlineTaskReflectionsQuery.completedTaskSumEgt] 课程目录任务-完成数量-大于
   * @param [exportCourseOutlineTaskReflectionsQuery.filename] 不带后缀文件名
   * @param [exportCourseOutlineTaskReflectionsQuery.ridIn]
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  exportCourseOutlineTaskReflections(
    id: number,
    exportCourseOutlineTaskReflectionsQuery?: ExportCourseOutlineTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      courseApi.exportCourseOutlineTaskReflectionsConfig(
        id,
        exportCourseOutlineTaskReflectionsQuery,
        options
      )
    ) as any
  },

  useExportCourseOutlineTaskReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: ExportCourseOutlineTaskReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      courseApi.exportCourseOutlineTaskReflections,
      'Course.exportCourseOutlineTaskReflections',
      queryOptions,
      id,
      query
    )
  },

  modifyCourseTaskReviewedConfig(
    id: number,
    quizTaskReviewedReq: QuizTaskReviewedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/activity-tasks/{id}/reviewed',
      {
        id,
      },
      null,
      quizTaskReviewedReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】把测验任务状态改为已批阅
   * @param id
   * @param quizTaskReviewedReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  modifyCourseTaskReviewed(
    id: number,
    quizTaskReviewedReq: QuizTaskReviewedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      courseApi.modifyCourseTaskReviewedConfig(id, quizTaskReviewedReq, options)
    ) as any
  },

  useModifyCourseTaskReviewed(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: QuizTaskReviewedReq }) =>
        courseApi.modifyCourseTaskReviewed(id, payload),
      mutationOptions
    )
  },

  queryCourseReflectionCompletedConfig(
    bizId: number,
    queryCourseReflectionCompletedQuery?: QueryCourseReflectionCompletedQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/course/completed',
      {
        bizId,
      },
      queryCourseReflectionCompletedQuery
    )
  },

  /**
   * @summary 【前/后台】习题-查询提交的答案
   *  - 后台：根据提交人id查询提交答案
   *  - 前台：根据token解析的id查询提交答案
   * @param bizId 目录任务id
   * @param [queryCourseReflectionCompletedQuery]
   * @param [queryCourseReflectionCompletedQuery.isForeground] 是否是前台请求
   * 前台根据token查结果
   * 后台根据rid查结果
   * @param [queryCourseReflectionCompletedQuery.rid]
   * @param [options]
   * @return AxiosResponsePromise<ReflectionAnswer>
   */
  queryCourseReflectionCompleted(
    bizId: number,
    queryCourseReflectionCompletedQuery?: QueryCourseReflectionCompletedQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<ReflectionAnswer> {
    return axios(
      courseApi.queryCourseReflectionCompletedConfig(
        bizId,
        queryCourseReflectionCompletedQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new ReflectionAnswer(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryCourseReflectionCompleted(
    {
      bizId,
      query,
    }: {
      bizId: number
      query?: QueryCourseReflectionCompletedQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<ReflectionAnswer> {
    return useQueryApi<ReflectionAnswer>(
      courseApi.queryCourseReflectionCompleted,
      'Course.queryCourseReflectionCompleted',
      queryOptions,
      bizId,
      query
    )
  },

  batchSortOutlineTaskConfig(
    sortBlock: SortBlock[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/outline/tasks/$batch/sort',
      {},
      null,
      sortBlock,
      {
        format: 'json',
        isArray: true,
        defaults: {
          parentId: (v: any) => v || 0,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】批量排序课程目录任务
   * @param sortBlock
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchSortOutlineTask(
    sortBlock: SortBlock[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      courseApi.batchSortOutlineTaskConfig(sortBlock, options)
    ) as any
  },

  useBatchSortOutlineTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: SortBlock[]) => courseApi.batchSortOutlineTask(payload),
      mutationOptions
    )
  },

  createCourseOutlineConfig(
    outlineReq: OutlineReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/course/outlines',
      {},
      null,
      outlineReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          parentId: (v: any) => v || 0,
          activityTaskId: (v: any) => v || 0,
          name: (v: any) => v || '',
          weight: (v: any) => v || 0,
          treeNodeId: (v: any) => v || 0,
          tasks: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】课程-新增目录
   *  - 支持创建目录任务
   * @param outlineReq
   * @param [options]
   * @return AxiosResponsePromise<Outline>
   */
  createCourseOutline(
    outlineReq: OutlineReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Outline> {
    return axios(courseApi.createCourseOutlineConfig(outlineReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Outline(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateCourseOutline(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: OutlineReq) => courseApi.createCourseOutline(payload),
      mutationOptions
    )
  },

  batchSortOutlinesConfig(
    sortBlock: SortBlock[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/outlines/$batch/sort',
      {},
      null,
      sortBlock,
      {
        format: 'json',
        isArray: true,
        defaults: {
          parentId: (v: any) => v || 0,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】批量排序课程目录
   * @param sortBlock
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchSortOutlines(
    sortBlock: SortBlock[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(courseApi.batchSortOutlinesConfig(sortBlock, options)) as any
  },

  useBatchSortOutlines(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: SortBlock[]) => courseApi.batchSortOutlines(payload),
      mutationOptions
    )
  },

  queryExerciseReviewedConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/course/outlines/paper/{id}/reviewed', {
      id,
    })
  },

  /**
   * @summary 【后台】课程-习题-查询习题有没有被批阅
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<ExerciserReviewed>
   */
  queryExerciseReviewed(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<ExerciserReviewed> {
    return axios(courseApi.queryExerciseReviewedConfig(id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new ExerciserReviewed(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryExerciseReviewed(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<ExerciserReviewed> {
    return useQueryApi<ExerciserReviewed>(
      courseApi.queryExerciseReviewed,
      'Course.queryExerciseReviewed',
      queryOptions,
      id
    )
  },

  deleteCourseOutlineConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/course/outlines/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】课程-删除目录
   *  - 删除目录
   *  - 删除目录任务
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteCourseOutline(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(courseApi.deleteCourseOutlineConfig(id, options)) as any
  },

  useDeleteCourseOutline(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => courseApi.deleteCourseOutline(id),
      mutationOptions
    )
  },

  updateCourseOutlineConfig(
    id: number,
    outlineReq: OutlineReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/outlines/{id}',
      {
        id,
      },
      null,
      outlineReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          parentId: (v: any) => v || 0,
          activityTaskId: (v: any) => v || 0,
          name: (v: any) => v || '',
          weight: (v: any) => v || 0,
          treeNodeId: (v: any) => v || 0,
          tasks: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【后台】课程-编辑目录
   *  - 不支持编辑目录任务
   * @param id
   * @param outlineReq
   * @param [options]
   * @return AxiosResponsePromise<Outline>
   */
  updateCourseOutline(
    id: number,
    outlineReq: OutlineReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Outline> {
    return axios(
      courseApi.updateCourseOutlineConfig(id, outlineReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Outline(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateCourseOutline(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: OutlineReq }) =>
        courseApi.updateCourseOutline(id, payload),
      mutationOptions
    )
  },

  updateExerciseViewedConfig(
    outlineTaskId: number,
    outlineId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/outlines/{outlineId}/activity-tasks/{id}/viewed',
      {
        outlineTaskId,
        outlineId,
        id,
      }
    )
  },

  /**
   * @summary 【前台】课程-习题-当学生查看习题后修改状态
   * @param outlineTaskId
   * @param outlineId
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  updateExerciseViewed(
    outlineTaskId: number,
    outlineId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      courseApi.updateExerciseViewedConfig(
        outlineTaskId,
        outlineId,
        id,
        options
      )
    ) as any
  },

  useUpdateExerciseViewed(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        outlineTaskId,
        outlineId,
        id,
      }: {
        outlineTaskId: number
        outlineId: number
        id: number
      }) => courseApi.updateExerciseViewed(outlineTaskId, outlineId, id),
      mutationOptions
    )
  },

  createCourseOutlineTaskConfig(
    id: number,
    courseOutlineTaskReq: CourseOutlineTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/course/outlines/{id}/outline-tasks',
      {
        id,
      },
      null,
      courseOutlineTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          name: (v: any) => v || '',
          type: (v: any) => v || null,
          allowAttachmentDown: (v: any) => v || false,
          attachments: (v: any) => v || [],
          paperId: (v: any) => v || 0,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】课程-创建目录任务
   *  - 关联试卷，根据关联试卷拷贝试卷
   * @param id
   * @param courseOutlineTaskReq
   * @param [options]
   * @return AxiosResponsePromise<CourseOutlineTask>
   */
  createCourseOutlineTask(
    id: number,
    courseOutlineTaskReq: CourseOutlineTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<CourseOutlineTask> {
    return axios(
      courseApi.createCourseOutlineTaskConfig(id, courseOutlineTaskReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new CourseOutlineTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateCourseOutlineTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: CourseOutlineTaskReq }) =>
        courseApi.createCourseOutlineTask(id, payload),
      mutationOptions
    )
  },

  deleteCourseOutlineTaskConfig(
    taskId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/course/outlines/{id}/outline-tasks/{taskId}',
      {
        taskId,
        id,
      }
    )
  },

  /**
   * @summary 【后台】课程-删除目录任务
   * @param taskId
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteCourseOutlineTask(
    taskId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      courseApi.deleteCourseOutlineTaskConfig(taskId, id, options)
    ) as any
  },

  useDeleteCourseOutlineTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ taskId, id }: { taskId: number; id: number }) =>
        courseApi.deleteCourseOutlineTask(taskId, id),
      mutationOptions
    )
  },

  updateCourseOutlineTaskConfig(
    taskId: number,
    id: number,
    courseOutlineTaskReq: CourseOutlineTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/outlines/{id}/outline-tasks/{taskId}',
      {
        taskId,
        id,
      },
      null,
      courseOutlineTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          name: (v: any) => v || '',
          type: (v: any) => v || null,
          allowAttachmentDown: (v: any) => v || false,
          attachments: (v: any) => v || [],
          paperId: (v: any) => v || 0,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】课程-编辑目录任务
   *  - 编辑目录任务
   *  - 重置用户改目录任务完成状态
   * @param taskId
   * @param id
   * @param courseOutlineTaskReq
   * @param [options]
   * @return AxiosResponsePromise<CourseOutlineTask>
   */
  updateCourseOutlineTask(
    taskId: number,
    id: number,
    courseOutlineTaskReq: CourseOutlineTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<CourseOutlineTask> {
    return axios(
      courseApi.updateCourseOutlineTaskConfig(
        taskId,
        id,
        courseOutlineTaskReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new CourseOutlineTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateCourseOutlineTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        taskId,
        id,
        payload,
      }: {
        taskId: number
        id: number
        payload: CourseOutlineTaskReq
      }) => courseApi.updateCourseOutlineTask(taskId, id, payload),
      mutationOptions
    )
  },

  moveCourseOutlineTaskConfig(
    taskId: number,
    id: number,
    courseActivityTaskMoveReq: CourseActivityTaskMoveReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/outlines/{id}/outline-tasks/{taskId}/move',
      {
        taskId,
        id,
      },
      null,
      courseActivityTaskMoveReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】课程-移动目录任务到其他的目录里
   * @param taskId 目录任务id
   * @param id
   * @param courseActivityTaskMoveReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  moveCourseOutlineTask(
    taskId: number,
    id: number,
    courseActivityTaskMoveReq: CourseActivityTaskMoveReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      courseApi.moveCourseOutlineTaskConfig(
        taskId,
        id,
        courseActivityTaskMoveReq,
        options
      )
    ) as any
  },

  useMoveCourseOutlineTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        taskId,
        id,
        payload,
      }: {
        taskId: number
        id: number
        payload: CourseActivityTaskMoveReq
      }) => courseApi.moveCourseOutlineTask(taskId, id, payload),
      mutationOptions
    )
  },

  modifyOutlineTaskCompletedConfig(
    taskId: number,
    id: number,
    outlineTaskCompletedReq: OutlineTaskCompletedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/course/outlines/{id}/tasks/{taskId}/completed',
      {
        taskId,
        id,
      },
      null,
      outlineTaskCompletedReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          isViewed: (v: any) => v || false,
          viewedVideoAt: (v: any) => v || '',
          answers: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【前台】课程-完成目录任务
   *    - 如用户不在课程任务人员列表内，将报错
   * @param taskId 目录关联的目录任务
   * @param id
   * @param outlineTaskCompletedReq
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection>
   */
  modifyOutlineTaskCompleted(
    taskId: number,
    id: number,
    outlineTaskCompletedReq: OutlineTaskCompletedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<TaskReflection> {
    return axios(
      courseApi.modifyOutlineTaskCompletedConfig(
        taskId,
        id,
        outlineTaskCompletedReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new TaskReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useModifyOutlineTaskCompleted(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        taskId,
        id,
        payload,
      }: {
        taskId: number
        id: number
        payload: OutlineTaskCompletedReq
      }) => courseApi.modifyOutlineTaskCompleted(taskId, id, payload),
      mutationOptions
    )
  },

  modifyOutlineTaskSaveConfig(
    taskId: number,
    id: number,
    outlineTaskCompletedReq: OutlineTaskCompletedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/course/outlines/{id}/tasks/{taskId}/save',
      {
        taskId,
        id,
      },
      null,
      outlineTaskCompletedReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          isViewed: (v: any) => v || false,
          viewedVideoAt: (v: any) => v || '',
          answers: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【前台】课程-保存习题答案
   *    - 如用户不在课程任务人员列表内，将报错
   * @param taskId 目录关联的目录任务
   * @param id
   * @param outlineTaskCompletedReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  modifyOutlineTaskSave(
    taskId: number,
    id: number,
    outlineTaskCompletedReq: OutlineTaskCompletedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      courseApi.modifyOutlineTaskSaveConfig(
        taskId,
        id,
        outlineTaskCompletedReq,
        options
      )
    ) as any
  },

  useModifyOutlineTaskSave(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        taskId,
        id,
        payload,
      }: {
        taskId: number
        id: number
        payload: OutlineTaskCompletedReq
      }) => courseApi.modifyOutlineTaskSave(taskId, id, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(courseApi).forEach((api) => {
  // @ts-ignore
  courseApi[api]._name_ = `Course.${api}`
})
